// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — the mount-wiring module: initAgentBridge (ABB-S7b)
// ═══════════════════════════════════════════════════════════════════════════
//
// `initAgentBridge(opts): AgentBridgeHandle` instantiates the shipped-but-inert
// S1–S6 agent-bridge client and wires ALL of its DI so the agent-through-safe-
// contour loop CAN run — but DOES NOT auto-connect. S7c's Connect UI calls
// `handle.connect(url, token)` to open the socket. Nothing here auto-mounts:
// importing this module constructs NOTHING (INV-OFF, ПРИОРИТЕТ #1) — the work
// happens only when `initAgentBridge` is CALLED, and the socket opens only on
// `handle.connect()`.
//
// WHERE IT LIVES (state/, NOT agentBridge/): this is the STORE-COUPLED side of the
// agent-bridge DI boundary — it imports `useEditorStore`, the safe contour, the
// ai-dispatch-context helpers, and the manifest builder. The whole `agentBridge/`
// graph is held STORE-FREE by an enforced grep gate
// (`agentBridge/__tests__/commandRun.test.ts` — "no agentBridge source module
// imports editorStore … the ONLY allowed shape is the closure S7 BUILDS in the
// editorStore mount and passes IN as a dep"). So this mount module is built HERE
// (the store-mount area, alongside `captureSnapshot.ts`) and injects every store-
// coupled dep into the agentBridge factories from the OUTSIDE. The store-free leaf
// graph (`agentBridge/*`, `ai/sceneSummaryProjector`, `ai/sceneQuery/*`) stays a
// leaf; this module is their project-OUT source.
//
// THE WIRING (every CommandRunHandlerDeps field → its store source) — see
// `buildCommandRunDeps` below. THE INVARIANTS the wiring encodes:
//   • SYNCHRONOUS audit emit — `onTurnAuditCommitted` calls the push-observers'
//     `emitTurnAuditOnCommit` SYNCHRONOUSLY (no await / microtask hop), so the
//     audit push is stamped with the current(=dispatch) epoch (S4's contract).
//   • ABORT SYMMETRY — both `client.disconnect()` AND a mid-turn `onClose` drop
//     fire `onAbortInProgressTurn` EXACTLY ONCE (the client guarantees single-fire
//     by construction — disconnect detaches handlers before close()). The bridge
//     self-abort is routed to the push-observers' demotion seam + the export abort.
//   • PAIRED bridge-turn markers — `beginBridgeTurn`/`endBridgeTurn` are BOTH
//     wired (the contour brackets them in a try/finally) — never one without the
//     other.
//   • CONTOUR is DI — the SAME `runAiCommandBatchThroughSafeContour` the panel +
//     window seam use is INJECTED as `runContour` (never imported by the S4 leaf).
//   • documentEpochCache.hydrate ⇔ onDocumentChanged — the client's adoption path
//     (welcome / session-changed) drives `onDocumentChanged`, wired to the cache's
//     `hydrate` (a real swap) / `reset` (a teardown, null belief); the state.read
//     handler binds each read to the SAME cache (the project-OUT ⇔ hydrate-IN
//     symmetry).
//
// INERT (Cat-B, default-OFF): `initAgentBridge` is exported but only CALLED by
// S7c's enable action; importing it has NO side effect (no top-level construction,
// no connect, no registry mutation). Today it has no caller in the app graph.
//
// DEP-GRAPH (ПРИОРИТЕТ #2): this module is the store-coupled SINK — it imports the
// agentBridge leaves (the factories), the store, the contour, the helpers. No
// agentBridge / ai-leaf module imports BACK into it (S7c references it from the
// store-mount, not from a leaf), so it adds no cycle. `npx depcruise` cycles=0.
// ═══════════════════════════════════════════════════════════════════════════

import type { AiDispatchContext } from "@dxf-ai-editor/ai-commands";
import {
  buildManifestCommands,
  computeManifestSchemaVersion,
} from "@dxf-ai-editor/ai-commands/manifest";

import {
  AGENT_BRIDGE_WS_URL,
  AgentBridgeClient,
  type AgentBridgeClientDeps,
  type AgentBridgeStatus,
  beginBridgeTurn as beginBridgeTurnMarker,
  type BridgeRole,
  type CommandRunHandlerDeps,
  createCommandRunHandler,
  createDocumentEpochCache,
  createExportDxfHandler,
  createPushObservers,
  type DocumentEpochCache,
  endBridgeTurn as endBridgeTurnMarker,
  type ExportDxfHandlerDeps,
  type LiveRevisions,
  type PushObserverHandle,
  type PushObserverSnapshot,
  type PushSink,
  releaseInFlight,
  type RpcHandler,
  type StagedAuditEntry,
  createStateReadHandler,
} from "../agentBridge";
import { registerSceneQueryProjectors } from "../ai/sceneQuery";
import {
  collectAiEntitySnapshot,
  collectAiInsertIds,
  resolveAiDispatchContext,
} from "../ai/aiDispatchContext";
import {
  runAiCommandBatchThroughSafeContour,
  type AiEntitySnapshotEntry,
} from "../ai/aiSafeContour";
import { useSysvarStore } from "../dynamicInput/sysvarStore";
import { captureSnapshot } from "./captureSnapshot";
import { useEditorStore } from "./editorStore";

/**
 * Whether `registerSceneQueryProjectors()` has already run this session. The
 * registration is idempotent (a re-register overwrites the prior body), but a flag
 * keeps a repeated `initAgentBridge` (an HMR remount / a reconnect that rebuilds the
 * handle) from re-walking the routes. Module-scoped (the projector registry is a
 * process global), NOT a closure (so two handles share the once-guard).
 */
let sceneQueryProjectorsRegistered = false;

/** Register the SCENE-QUERY route projectors exactly ONCE per session. */
function ensureSceneQueryProjectorsRegistered(): void {
  if (sceneQueryProjectorsRegistered) {
    return;
  }
  registerSceneQueryProjectors();
  sceneQueryProjectorsRegistered = true;
}

/** TEST-ONLY: reset the once-guard so a test can assert the single-registration. */
export function __resetSceneQueryRegistrationForTest(): void {
  sceneQueryProjectorsRegistered = false;
}

/**
 * The handle `initAgentBridge` returns. `connect`/`disconnect` drive the client's
 * socket lifecycle (S7c's connectionStore calls them); `client` exposes the live
 * status/role/epoch for the indicator. The handle does NOT auto-connect — `connect`
 * is the only thing that opens a socket (INV-OFF, mirrored from the client).
 */
export interface AgentBridgeHandle {
  /** Open the loopback WS + pair with the token (the ONLY thing that opens a socket). */
  connect: (url: string, token: string) => void;
  /** Tear down the socket (suppresses reconnect; aborts an in-flight turn; stops observers). */
  disconnect: () => void;
  /** The live client (status / role / epoch for the S7c indicator). */
  client: AgentBridgeClient;
  /**
   * The WS URL this handle's client was CONSTRUCTED with (@codex P2 — finding 1). The
   * client captures `wsUrl` at construction (it cannot be re-pointed by `connect`), so the
   * connectionStore must build the handle with the requested URL and rebuild when the URL
   * changes — `wsUrl` lets the store detect a url change + assert the reported `companionUrl`
   * equals the socket's ACTUAL target (never report a url the socket did not open).
   */
  readonly wsUrl: string;
}

/** The options `initAgentBridge` accepts (all DI-overridable for deterministic tests). */
export interface InitAgentBridgeOptions {
  /**
   * The socket factory (DI; default `new WebSocket` via the client's default). Tests
   * inject a mock-socket factory so no real network is opened.
   */
  readonly socketFactory?: AgentBridgeClientDeps["socketFactory"];
  /** Reconnect tuning (DI for deterministic tests). */
  readonly reconnect?: AgentBridgeClientDeps["reconnect"];
  /** The connect URL (DI; defaults to the client's hardcoded loopback URL). */
  readonly wsUrl?: string;
  /** Surface the connection status to S7c's UI (the connectionStore wires this). */
  readonly onStatusChange?: (status: AgentBridgeStatus) => void;
  /**
   * The browser `performance.now`-domain clock for the command.run deadline (DI for
   * deterministic tests; default `() => performance.now()` inside the handler).
   */
  readonly now?: () => number;
  /** Diagnostics for the hello frame (opaque, capped companion-side). */
  readonly getClientInfo?: AgentBridgeClientDeps["getClientInfo"];
  /**
   * The safe-contour fn (DI; DEFAULT the real `runAiCommandBatchThroughSafeContour`).
   * Production omits it → the real contour is wired (the CONTOUR-is-DI invariant: the
   * SAME fn the panel + window seam use). A test injects a spy to assert the bridge
   * routes a command.run THROUGH the injected contour (never a parallel write path).
   */
  readonly runContour?: CommandRunHandlerDeps["runContour"];
  /**
   * The exported-DXF-text source for the export.dxf producer (DI; DEFAULT the live
   * `window.__editDwgExportedDxfText` App seam → `undefined` when absent, an EXPLICIT
   * not-wired signal the handler surfaces as `export-error` — @codex C). A test injects a
   * fixed string so the producer streams deterministically with no real App ref.
   */
  readonly getExportedDxfText?: ExportDxfHandlerDeps["getExportedDxfText"];
  /**
   * The export producer's cooperative event-loop yield (DI; DEFAULT setTimeout 0). A
   * test injects a resolved promise so the stream completes synchronously.
   */
  readonly yieldToEventLoop?: ExportDxfHandlerDeps["yieldToEventLoop"];
}

// ── store-source helpers (the ONLY allowed getState().dispatch — outside agentBridge) ──

/** Read the live document identity for the hello frame (S3 reads it at open). */
function getHelloDocument(): { documentId: string | null; documentEpoch: number | null } {
  const store = useEditorStore.getState();
  return {
    documentId: store.geometryDocument.id,
    documentEpoch: store.revisions.documentEpoch,
  };
}

/** Read the four browser-minted revision counters the precondition gate adjudicates. */
function getRevisions(): LiveRevisions {
  const { revisions } = useEditorStore.getState();
  return {
    snapshotRevision: revisions.snapshotRevision,
    geometryRevision: revisions.geometryRevision,
    tableRevision: revisions.tableRevision,
    contextRevision: revisions.contextRevision,
  };
}

/**
 * Read the live manifest `schemaVersion` (the value the agent echoes as
 * `manifestVersion`). Computed from the canonical command set the SAME way the
 * serving layer does (`computeManifestSchemaVersion(buildManifestCommands())`,
 * `buildManifest.ts`) — one truth so the browser gate + the served manifest never
 * drift. Deterministic + byte-stable for a given build (a pure FNV-1a hash, no I/O).
 */
function getManifestVersion(): string {
  return computeManifestSchemaVersion(buildManifestCommands());
}

/** Read the live audit high-water (`state.audit.nextSequence`, the EXCLUSIVE next-seq). */
function getAuditHighWater(): number {
  return useEditorStore.getState().state.audit.nextSequence;
}

/** Read the live audit-event RING (newest-first — the shape `appendAuditEvent` prepends). */
function getAuditRing(): readonly StagedAuditEntry[] {
  return useEditorStore.getState().state.audit.events as readonly StagedAuditEntry[];
}

/**
 * Build the runtime AiDispatchContext the contour translator needs from the LIVE
 * shell state — the SAME one-truth derivation (`resolveAiDispatchContext` +
 * the live MIRRTEXT base) the window AI entry points + the CC-2d panel use, so the
 * bridge turn resolves active-space / paper-space / sysvars identically. No override
 * (the forwarded payload carries none — INV-1).
 */
function getAiContext(): unknown {
  return resolveAiDispatchContext(
    useEditorStore.getState().state,
    undefined,
    { mirrtext: useSysvarStore.getState().values.MIRRTEXT },
  );
}

/** Read every INSERT (Block) entity id in the live document, in document order. */
function resolveInsertIds(): readonly string[] {
  return collectAiInsertIds(useEditorStore.getState().geometryDocument);
}

/**
 * T2 — snapshot every entity (`{ id, fingerprint }`) in the live document so the
 * contour can derive the turn's `createdEntityIds` / `affectedEntityIds`. SAME
 * one-truth reader (`collectAiEntitySnapshot`) the window AI entry points + the AI
 * drafting panel use. Returned as `readonly unknown[]` to match the S4 structural
 * seam (the agentBridge leaf does not depend on `aiSafeContour`'s row type).
 */
function snapshotEntities(): readonly unknown[] {
  return collectAiEntitySnapshot(useEditorStore.getState().geometryDocument);
}

/** Build the minimal push-observer snapshot from the live store (the only store coupling). */
function getPushObserverSnapshot(): PushObserverSnapshot {
  const store = useEditorStore.getState();
  return {
    selectionEntityIds: store.state.selection.entityIds,
    documentId: store.geometryDocument.id,
    documentEpoch: store.revisions.documentEpoch,
    auditRingNewestFirst: store.state.audit.events as readonly StagedAuditEntry[],
  };
}

/**
 * Build the full `CommandRunHandlerDeps` — EVERY field mapped to its live-store
 * source (the store-coupled side of the S4 DI boundary). Declared here so the wiring
 * is one explicit object the tests can audit field-by-field.
 *
 * The two invariant-bearing fields:
 *   • `onTurnAuditCommitted` calls `pushObservers.emitTurnAuditOnCommit` SYNCHRONOUSLY
 *     (no await/microtask) — the S4 handler invokes it before its `await
 *     computeBatchHash`, so the audit push is stamped with the current(=dispatch)
 *     epoch (the SYNCHRONOUS-EMIT CONTRACT).
 *   • `beginBridgeTurn`/`endBridgeTurn` are BOTH wired (the contour brackets them in
 *     a try/finally) — paired by construction.
 */
function buildCommandRunDeps(
  pushObservers: PushObserverHandle,
  now: (() => number) | undefined,
  runContourOverride: CommandRunHandlerDeps["runContour"] | undefined,
): CommandRunHandlerDeps {
  return {
    // CONTOUR is DI — the SAME safe contour the panel + window seam use (never imported
    // by the S4 leaf). The thin pass-through forwards VERBATIM to that ONE fn (NOT a
    // logic copy): the only reason it is wrapped is the structural option-type seam (S4's
    // `RunContour.options.context` is `unknown`; the real contour's is the concrete
    // `AiDispatchContext`). It calls the identical `runAiCommandBatchThroughSafeContour`
    // — same parse / guard / grouped-undo / dispatch / audit, INV-WRITE. A test may
    // inject a spy via `runContourOverride` to assert routing-through-the-contour.
    runContour:
      runContourOverride ??
      ((proposed, dispatch, options) =>
        runAiCommandBatchThroughSafeContour(proposed, dispatch, {
          context: options.context as AiDispatchContext | undefined,
          ...(options.resolveInsertIds !== undefined
            ? { resolveInsertIds: options.resolveInsertIds }
            : {}),
          // T2 — the structural seam types the snapshot rows as `unknown`; the real
          // contour's `snapshotEntities` is `() => readonly AiEntitySnapshotEntry[]`.
          // Narrow here (the mount supplies the concrete `collectAiEntitySnapshot`).
          ...(options.snapshotEntities !== undefined
            ? {
                snapshotEntities:
                  options.snapshotEntities as () => readonly AiEntitySnapshotEntry[],
              }
            : {}),
          ...(options.label !== undefined ? { label: options.label } : {}),
        })),
    // The CONTOUR-BOUND raw dispatch sink. The S4 handler WRAPS this (the pre-commit
    // self-check) and passes the WRAPPER into the contour — it never holds a raw
    // getState().dispatch handle (that handle lives HERE, outside the agentBridge graph).
    dispatchToContour: (command) => useEditorStore.getState().dispatch(command as never),
    getRevisions,
    getManifestVersion,
    getAuditHighWater,
    getAuditRing,
    getAiContext,
    resolveInsertIds,
    // T2 — the per-turn entity snapshot reader (created/affected id surfacing).
    snapshotEntities,
    // The role/epoch authority (the client's SessionTracker, surfaced via the client).
    isCurrentActiveEpoch: () => clientRef.current?.isCurrentActiveEpoch() ?? false,
    getCurrentEpoch: () => clientRef.current?.getCurrentEpoch() ?? null,
    ...(now !== undefined ? { now } : {}),
    // PAIRED bridge-turn markers (the contour brackets them try/finally — both wired).
    beginBridgeTurn: () => beginBridgeTurnMarker(),
    endBridgeTurn: () => endBridgeTurnMarker(),
    // SYNCHRONOUS audit emit: fire the push-observers' audit emit SYNCHRONOUSLY at commit
    // (no await/microtask) so the push is stamped with the current(=dispatch) epoch.
    onTurnAuditCommitted: (snapshot) => {
      pushObservers.emitTurnAuditOnCommit(snapshot.auditEvents);
    },
  };
}

/**
 * A late-bound client reference so the deps built BEFORE the client (the command.run
 * deps, the push sink, the export deps — all reference the client's role/epoch
 * authority + its wire senders) can reach the client once it exists. Module-scoped: a
 * session mounts ONE bridge (the connectionStore builds + reuses a single handle), so a
 * single holder is correct; the LAST `initAgentBridge` wins (an HMR remount rebuilds the
 * handle + re-points this). Captured by reference in the dep closures, read lazily at
 * call time (never at build time) so the build-before-construct order is safe.
 */
const clientRef: { current: AgentBridgeClient | null } = { current: null };

/**
 * Instantiate the agent-bridge client + wire ALL the DI. Returns the handle the S7c
 * connectionStore drives. INERT until `handle.connect(url, token)` is called (the
 * client opens NO socket on construction — INV-OFF). Idempotent registration of the
 * SCENE-QUERY projectors (once per session).
 *
 * The wiring order matters:
 *   1. register the SCENE-QUERY projectors (once);
 *   2. build the documentEpochCache + the 3 handlers (state.read binds the cache);
 *   3. build the push observers (their sink reads the client, late-bound);
 *   4. construct the client with handlers + onDocumentChanged→hydrate +
 *      onAbortInProgressTurn + onRoleChange→demotion + the export control router.
 */
export function initAgentBridge(opts: InitAgentBridgeOptions = {}): AgentBridgeHandle {
  // 1. Register the SCENE-QUERY route projectors ONCE (idempotent; a no-op on remount).
  ensureSceneQueryProjectorsRegistered();

  // 2. The in-bridge documentEpoch belief cache (1A) — the state.read handler binds
  //    each read to it; the client's adoption path hydrates it (the symmetry).
  const documentEpochCache: DocumentEpochCache = createDocumentEpochCache();

  // The state.read handler binds to the SAME cache the adoption path hydrates.
  const stateReadHandler: RpcHandler = createStateReadHandler({
    captureSnapshot,
    documentEpochCache,
  });

  // The export.dxf streaming producer (+ its control-frame router + teardown abort).
  const exportDxf = createExportDxfHandler(
    buildExportDxfDeps(opts.getExportedDxfText, opts.yieldToEventLoop),
  );

  // The SINGLE in-flight-abort function. Three paths invoke it (the abort-symmetry
  // invariant): a socket DROP (`onClose`), a deliberate `disconnect()` mid-turn (BOTH
  // route through the client's `onAbortInProgressTurn` DI — fired EXACTLY ONCE by the
  // client), AND a wire-driven DEMOTION (`onDemotion`, a session-changed / superseding
  // welcome that flips isCurrentActiveEpoch with no store mutation). It:
  //   • forces the export producer's teardown abort (a PAUSED producer parked on an
  //     await-credit promise that can never receive `export-cancel` — the wedge break);
  //   • releases a possibly-leaked bridge in-flight latch so the next turn is not blocked
  //     (idempotent — `releaseInFlight` no-ops if the bridge is not the holder).
  // The contour ROLLBACK itself is the S4 pre-commit self-check (it re-reads epoch/role
  // at commit and aborts the open batch) — this function does the AROUND-the-turn cleanup.
  const abortInFlightTurn = (): void => {
    exportDxf.abort();
    releaseInFlight("bridge");
  };

  // 3. The push observers — their sink reads the client (late-bound via clientRef) for
  //    the active-epoch gate + drives the wire via the client's push/control senders.
  //    Built BEFORE the command.run deps so the audit-emit callback can reference it.
  const pushSink: PushSink = {
    isCurrentActiveEpoch: () => clientRef.current?.isCurrentActiveEpoch() ?? false,
    sendPush: (event, payload) => clientRef.current?.sendPush(event, payload),
    sendDocumentChanged: (documentId, documentEpoch) =>
      clientRef.current?.sendDocumentChanged(documentId, documentEpoch),
    // A detected demotion fires the in-flight self-abort (the SAME cleanup the drop fires).
    onDemotion: abortInFlightTurn,
  };
  const pushObservers: PushObserverHandle = createPushObservers({
    store: { getSnapshot: getPushObserverSnapshot, subscribe: subscribeStore },
    sink: pushSink,
    getCurrentEpoch: () => clientRef.current?.getCurrentEpoch() ?? null,
  });

  // The command.run handler — the FULL DI surface (every field → its store source).
  const commandRunHandler: RpcHandler = createCommandRunHandler(
    buildCommandRunDeps(pushObservers, opts.now, opts.runContour),
  );

  // 4. Construct the client. DEFAULT-OFF: this opens NO socket — `connect` does.
  const client = new AgentBridgeClient({
    getHelloDocument,
    ...(opts.getClientInfo !== undefined ? { getClientInfo: opts.getClientInfo } : {}),
    handlers: {
      "command.run": commandRunHandler,
      "state.read": stateReadHandler,
      "export.dxf": exportDxf.handler,
      // manifest.read is NOT wired here (a dedicated MANIFEST slice owns it) — the
      // client replies `browser-dispatch-error{not yet wired}` (never a silent drop).
    },
    onExportControl: exportDxf.onControl,
    onAbortInProgressTurn: abortInFlightTurn,
    // documentEpochCache.hydrate ⇔ onDocumentChanged: a welcome/session-changed adoption
    // hydrates the belief; a teardown (null belief) resets it. The state.read handler
    // binds to the SAME cache (the project-OUT ⇔ hydrate-IN symmetry).
    //
    // This is ALSO the POST-SETTLE SELECTION-FLUSH hook (@codex P2 — the welcome-ordering
    // fix). The client fires `onDocumentChanged` (→ `notifyDocumentBelief`) on EVERY adopted
    // welcome AFTER `setStatus(active)` AND AFTER `refreshDocumentIdentityIfStale` has sent
    // the stale-on-promotion `document-changed` re-key (`agentBridgeClient.onWelcome` steps
    // — adopt→setStatus→refresh→notify). By WS-FIFO that re-key frame already left the socket
    // before the flush's selection push, so the selection lands UNDER THE SETTLED document.
    // `flushSelectionForSettledDocument` resets the selection high-water on a re-key (so the
    // current selection re-pushes under the NEW document even if the membership is unchanged)
    // then flushes EXACTLY ONCE (role/pairing/membership-gated). A teardown belief (all-null)
    // takes the `reset()` branch and is NEVER flushed.
    onDocumentChanged: (belief) => {
      if (belief.documentId === null && belief.documentEpoch === null && belief.sessionEpoch === null) {
        documentEpochCache.reset();
      } else {
        documentEpochCache.hydrate(belief);
        // Flush the PENDING selection under the now-settled document (POST-re-key — this
        // fires AFTER refreshDocumentIdentityIfStale in onWelcome, so the WS-FIFO re-key
        // frame precedes the selection push). Covers BOTH the no-doc-change become-active
        // case (live doc unchanged → a plain flush) and the stale-doc re-key case (the live
        // doc differs from the last push's doc → reset the high-water + re-push under it).
        // Keyed internally on the LIVE local document (not this lagging belief).
        pushObservers.flushSelectionForSettledDocument();
      }
    },
    // A role/epoch change (a session-changed / superseding welcome that flips
    // isCurrentActiveEpoch with NO store mutation) drives the demotion re-check
    // IMMEDIATELY — the wire-driven demotion path (idempotent with the store check).
    //
    // The PENDING-SELECTION flush is NOT done here (@codex P2 — the welcome-ordering fix):
    // `SessionTracker.adoptWelcome` fires `onRoleChange` BEFORE `onWelcome` runs
    // `refreshDocumentIdentityIfStale`, so an `onRoleChange` flush would push the selection
    // under the OLD (stale-on-promotion) companion document; the subsequent `document-changed`
    // re-key would then re-key WITHOUT the selection (the companion consumed the diff under the
    // old document, and the high-water was already advanced so nothing re-pushes). The flush is
    // wired into `onDocumentChanged` instead (the POST-settle hook above) so the selection is
    // pushed EXACTLY ONCE, UNDER THE SETTLED document identity, for both the no-doc-change
    // become-active case and the stale-doc re-key case. (The document-changed swap is not
    // stranded the same way: the client re-asserts the LIVE document on every active welcome via
    // `refreshDocumentIdentityIfStale` — `agentBridgeClient.ts` — reading the live
    // `getHelloDocument()`, not a deferred pending high-water.)
    onRoleChange: (_role: BridgeRole | null, _sessionEpoch: number | null) => {
      pushObservers.checkDemotionNow();
    },
    ...(opts.onStatusChange !== undefined ? { onStatusChange: opts.onStatusChange } : {}),
    ...(opts.socketFactory !== undefined ? { socketFactory: opts.socketFactory } : {}),
    ...(opts.reconnect !== undefined ? { reconnect: opts.reconnect } : {}),
    ...(opts.wsUrl !== undefined ? { wsUrl: opts.wsUrl } : {}),
  });
  clientRef.current = client;

  // [@codex P2 — finding 1] The URL the client's socket will ACTUALLY open. The client
  // captures `wsUrl` at construction (the default loopback URL when `opts.wsUrl` is
  // omitted); resolve the SAME value here so the handle reports the socket's real target
  // and the connectionStore can detect a URL change + report a truthful `companionUrl`.
  const resolvedWsUrl = opts.wsUrl ?? AGENT_BRIDGE_WS_URL;

  return {
    wsUrl: resolvedWsUrl,
    connect: (url, token) => {
      // [@codex P2 — finding 1] The client captured `wsUrl` at CONSTRUCTION; `connect`
      // CANNOT re-point the socket. The connectionStore builds this handle with the
      // requested URL (and rebuilds when the URL changes), so `url` here MUST equal the
      // constructed `resolvedWsUrl`. A mismatch means the store would report a URL the
      // socket never opens — surface it fail-closed (throw) rather than silently connect
      // to the wrong target. The token rides the hello frame.
      if (url !== resolvedWsUrl) {
        throw new Error(
          `agent-bridge: handle built for "${resolvedWsUrl}" cannot connect to "${url}" ` +
            `(the client captures wsUrl at construction; rebuild the handle for the new URL)`,
        );
      }
      client.connect(token);
    },
    disconnect: () => {
      // The client's disconnect() fires `onAbortInProgressTurn` (→ abortInFlightTurn)
      // EXACTLY ONCE (single-fire by construction); then stop the observers so a later
      // store tick never drives a torn-down sink.
      client.disconnect();
      pushObservers.stop();
    },
    client,
  };
}

/**
 * Build the export.dxf producer DI from the live App/store. The DXF text source +
 * snapshotRevision + the chunk-send sink + the epoch/liveness predicate — all read
 * from the live client/store at call time. The chunk-send + liveness predicate are
 * late-bound to the client via `clientRef`.
 */
function buildExportDxfDeps(
  getExportedDxfTextOverride: ExportDxfHandlerDeps["getExportedDxfText"] | undefined,
  yieldToEventLoopOverride: ExportDxfHandlerDeps["yieldToEventLoop"] | undefined,
): ExportDxfHandlerDeps {
  return {
    // The exported DXF text source. S7c supplies `App.tsx`'s `exportedDxfTextRef.current()`
    // via the `__editDwgExportedDxfText` window seam once the App wires it; until then
    // `readExportedDxfText` returns `undefined` (@codex C) → the export handler surfaces an
    // EXPLICIT not-wired reject (`export-error`), NOT a silent empty-DXF success. A test
    // injects a fixed string via `getExportedDxfTextOverride`.
    getExportedDxfText: getExportedDxfTextOverride ?? (() => readExportedDxfText()),
    getSnapshotRevision: () => useEditorStore.getState().revisions.snapshotRevision,
    ...(yieldToEventLoopOverride !== undefined
      ? { yieldToEventLoop: yieldToEventLoopOverride }
      : {}),
    // SEND a chunk via the client's gated public sender (welcomed + open + in-flight origin).
    sendChunk: (frame) => clientRef.current?.sendExportChunk(frame),
    // EPOCH-HALT ∧ liveness: the export is valid while the current epoch still equals the
    // origin epoch AND the socket is connected (active or observer — the export does not
    // require the active seat, only liveness + same origin epoch).
    isExportStillValid: (originEpoch) => {
      const c = clientRef.current;
      if (c === null) {
        return false;
      }
      return c.getCurrentEpoch() === originEpoch && c.getStatus().startsWith("connected");
    },
  };
}

/**
 * Read the exported DXF text from the live App seam. S7c wires `App.tsx`'s
 * `exportedDxfTextRef.current()` into the `window.__editDwgExportedDxfText` seam (a window
 * property consulted at call time, mirroring the established `editorRunAiCommandBatch`
 * window-seam pattern).
 *
 * [@codex C] Returns `undefined` when the seam is ABSENT / not a function (the App has NOT
 * wired it) — an EXPLICIT not-wired signal the export handler surfaces as a
 * `browser-dispatch-error` (→ `export-error` on the export wire), NOT a silent empty-string
 * export the agent would mistake for a real (empty) DXF. A seam that IS wired and returns a
 * string (even `""`, a genuinely empty document) returns that string verbatim. S7c is the
 * slice that wires `App.tsx`'s `exportedDxfTextRef` into this seam.
 */
function readExportedDxfText(): string | undefined {
  if (typeof window !== "undefined") {
    const ref = (window as unknown as Record<string, unknown>)["__editDwgExportedDxfText"];
    if (typeof ref === "function") {
      const text = (ref as () => unknown)();
      if (typeof text === "string") {
        return text;
      }
    }
  }
  return undefined; // the seam is absent / non-function / non-string → NOT wired (explicit).
}

/** Subscribe to live-store changes (the push-observers' only store coupling). */
function subscribeStore(listener: () => void): () => void {
  return useEditorStore.subscribe(listener);
}
